/* eslint-disable arrow-body-style */

import { CommonToast } from "@src/common/CommonToast";
import { InvocaPhoneNumber } from "@src/common/InvocaPhoneNumber/InvocaPhoneNumber";
import { LoadingOverlay } from "@src/common/LoadingOverlay";
import { FormSpacing, StyledDateInput } from "@src/common/styled-components";
import {
  APP_ROUTES,
  GenderIdentityOptions,
  isValidEmailFormat,
  LanguagesOptions,
  LegalSexOptions,
  MAX_PHONE_NUMBER_LENGTH,
  ONE_DAY_IN_MS,
  PHONE_VALIDATION_REGEX,
  PhoneTypeOptions,
  PreferredContactMethodOptions,
  PreferredPronounsOptions,
  YesNoOptions,
  ZIP_VALIDATION_REGEX,
} from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import {
  type FormDetails,
  useApplicationContext,
  useCanvasContext,
  useFormContext,
  useUTMContext,
} from "@src/context";
import { useCanvasPortalScrollFix } from "@src/hooks/useCanvasPortalScrollFix";
import { useIdleFormFlush } from "@src/hooks/useIdleFormFlush";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import { useResolvedPageLabels } from "@src/hooks/useResolvedPageLabels";
import { searchPatients, unfreezeAppointment } from "@src/services";
import { useCanvasPrefillSync } from "@src/services/canvas/prefill/useCanvasPrefillSync";
import ClientLogsService from "@src/services/client-logs.service";
import { PatientDataService } from "@src/services/patient-data.service";
import { evaluateAgeModalScenario } from "@src/utils/age-modal-guard";
import { navigateToContactUs } from "@src/utils/contact-us-navigation";
import {
  getTwelveYearsAgeDate,
  isValidDateOfBirth,
  parseDateForCalendarComparison,
  toAppointmentIsoDateTime,
  validateDateOfBirth,
} from "@src/utils/date-utils";
import { validateName } from "@src/utils/name-validation";
import { canRoleBypassGuardrails } from "@src/utils/role-guardrails";
import { isUnder18AgeRange } from "@src/utils/scheduling-metadata";
import { buildSubmittedDataSnapshot } from "@src/utils/submitted-data-snapshot";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { dayjs } from "@uhg-abyss/web/tools/dayjs";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Card } from "@uhg-abyss/web/ui/Card";
import { Heading } from "@uhg-abyss/web/ui/Heading";
import { IconSymbol } from "@uhg-abyss/web/ui/IconSymbol";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { TextInput } from "@uhg-abyss/web/ui/TextInput";
import { Tooltip } from "@uhg-abyss/web/ui/Tooltip";
import {
  type ChangeEvent,
  Fragment,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { patientInfoFormIds } from "../patientInformationNavigation";
import {
  type ParentGuardianFormData,
  type ParentGuardianFormErrors,
  ParentGuardianSection,
} from "./ParentGuardianSection";
import { PatientFoundNavigatorModal } from "./PatientFoundNavigatorModal";

const FormCard = styled(Card, {
  width: "100%",
  padding: "2rem",
});

const SearchErrorBanner = styled("div", {
  marginBottom: "1rem",
  padding: "1rem",
  backgroundColor: "$app.color.search.banner.error.background",
  border: "1px solid $app.color.search.banner.error.border",
  borderRadius: "4px",
  textAlign: "center",
  color: "$app.color.search.banner.error.text",
});

const DobInputWithAgeRangeTooltip = styled("div", {
  ".abyss-label-required-indicator": {
    display: "none",
  },
  flex: "1 1 0",
  minWidth: 0,
});

const DobLabelWithAgeRangeTooltip = styled("span", {
  alignItems: "center",
  display: "inline-flex",
  flexWrap: "wrap",
  gap: "$core.spacing.100",
});

const DobGuidanceIcon = styled("span", {
  alignItems: "center",
  background: "transparent",
  border: "0",
  color: "$app.color.text.link.rest.primary",
  cursor: "pointer",
  display: "inline-flex",
  padding: "0",
});

const DobRequiredIndicator = styled("span", {
  color: "$app.color.text.status.error",
});

const FormRow = styled("div", {
  display: "flex",
  gap: "$core.spacing.300",
  variants: {
    isMobile: {
      true: {
        flexDirection: "column",
      },
      false: {
        flexDirection: "row",
      },
    },
  },
});

const FormRowTopAligned = styled(FormRow, {
  "& > *": {
    flex: "1 1 0",
    minWidth: 0,
  },
  alignItems: "flex-start",
  flex: 1,
});

type GuardianPolicyHolderStatus = ParentGuardianFormData["isPolicyHolder"];

interface GuardianAutofillValues {
  firstName: string;
  lastName: string;
  dob: string;
  legalSex: string;
}

const normalizeGuardianPolicyHolderStatus = (
  value: boolean | undefined,
): GuardianPolicyHolderStatus => {
  if (value === undefined) {
    return "";
  }

  return value ? "yes" : "no";
};

const normalizeGuardianPolicyHolderInput = (value: string): GuardianPolicyHolderStatus => {
  const normalizedValue = value.toLowerCase();

  if (normalizedValue === "yes" || normalizedValue === "no") {
    return normalizedValue;
  }

  return "";
};

const normalizePolicyholderLegalSex = (value: string | undefined): string => {
  const genderMap: Record<string, string> = { Male: "M", Female: "F" };

  if (!value) {
    return "";
  }

  return genderMap[value] ?? value;
};

const resolveGuardianTransitionValue = (
  useGuardianDetails: boolean,
  guardianValue: string,
  shouldClear: boolean,
  previousValue: string,
): string => {
  if (useGuardianDetails && guardianValue) {
    return guardianValue;
  }
  if (shouldClear) {
    return "";
  }
  return previousValue;
};

const getAgeCutoffs = () => {
  const twelveCutoff = getTwelveYearsAgeDate();
  // Stryker disable next-line CallExpression,MethodExpression
  twelveCutoff.setHours(0, 0, 0, 0);
  return { twelveCutoff };
};

export const YourInfoForm = () => {
  const { data, setData, validateFooterCheckboxes } = useApplicationContext(); // Access Application context
  const { utmTags } = useUTMContext(); // Access UTM context
  const { setHandleSubmit } = useFormContext(); // Access Form context
  const { isInSalesforce: canvasIsInSalesforce } = useCanvasContext();
  const navigate = useNavigateWithUTM();
  const navTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Ref to store timeout ID

  useCanvasPortalScrollFix(canvasIsInSalesforce);

  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);

  const policyholderAutofillValues = useMemo<GuardianAutofillValues>(
    () => ({
      firstName: data.policyholderFirstName ?? "",
      lastName: data.policyholderLastName ?? "",
      dob: data.policyholderDOB ?? "",
      legalSex: normalizePolicyholderLegalSex(data.policyholderGender),
    }),
    [
      data.policyholderFirstName,
      data.policyholderLastName,
      data.policyholderDOB,
      data.policyholderGender,
    ],
  );

  const guardianAutofillValues = useMemo<GuardianAutofillValues>(
    () => ({
      firstName: data.guardianDetails?.firstName ?? "",
      lastName: data.guardianDetails?.lastName ?? "",
      dob: data.guardianDetails?.dob ?? "",
      legalSex: data.guardianDetails?.legalSex ?? "",
    }),
    [
      data.guardianDetails?.firstName,
      data.guardianDetails?.lastName,
      data.guardianDetails?.dob,
      data.guardianDetails?.legalSex,
    ],
  );

  // Helper function to extract initial policy holder status
  const getInitialPolicyHolderStatus = (): GuardianPolicyHolderStatus => {
    return normalizeGuardianPolicyHolderStatus(data.isGuardianPolicyHolder);
  };

  const guardianNameAutofillRef = useRef({
    wasAutofilled: data.isGuardianPolicyHolder === true,
    ...policyholderAutofillValues,
  });
  const previousGuardianPolicyHolderRef = useRef<GuardianPolicyHolderStatus>(
    getInitialPolicyHolderStatus(),
  );

  const [formData, setFormData] = useState({
    memberFirstName: data.memberDetails?.firstName || "",
    memberLastName: data.memberDetails?.lastName || "",
    dob: data.yourInfoForm?.submittedData?.dob || data.dob || "",
    memberGenderIdentity: data.memberDetails?.genderIdentity || "",
    memberLegalSex: data?.gender || data.memberDetails?.legalSex || "",
    memberPreferredPronouns: data.memberDetails?.preferredPronouns || "",
    memberLanguage: data.memberDetails?.language || "",
    memberStreetAddress1: data.memberDetails?.streetAddress1 || "",
    memberStreetAddress2: data.memberDetails?.streetAddress2 || "",
    memberCity: data.memberDetails?.city || data.address?.city || "",
    memberState: data.memberDetails?.state_code || data.address?.state_code || "",
    memberZipCode: data.memberDetails?.zip || data.zip || "",
    memberEmail: data.memberDetails?.email || "",
    memberPhone: data.memberDetails?.phone || "",
    memberPhoneType: data.memberDetails?.phoneType || "",
    memberPreferredContactMethod: data.memberDetails?.preferredContactMethod || "",
    memberLeaveVoicemailPreference: data.memberDetails?.voicemailPreference || "",
  });

  const [parentGuardianData, setParentGuardianData] = useState<ParentGuardianFormData>({
    isPolicyHolder: getInitialPolicyHolderStatus(),
    firstName: data.guardianDetails?.firstName || "",
    lastName: data.guardianDetails?.lastName || "",
    dob: data.guardianDetails?.dob || "",
    legalSex: data.guardianDetails?.legalSex || "",
    genderIdentity: data.guardianDetails?.genderIdentity || "",
    preferredPronouns: data.guardianDetails?.preferredPronouns || "",
    preferredLanguage: data.guardianDetails?.preferredLanguage || "",
    streetAddress1: data.guardianDetails?.streetAddress1 || "",
    streetAddress2: data.guardianDetails?.streetAddress2 || "",
    city: data.guardianDetails?.city || "",
    state: data.guardianDetails?.state || "",
    zipCode: data.guardianDetails?.zipCode || "",
    email: data.guardianDetails?.email || "",
    phone: data.guardianDetails?.phone || "",
    phoneType: data.guardianDetails?.phoneType || "",
    preferredContactMethod: data.guardianDetails?.preferredContactMethod || "",
    voicemailPreference: data.guardianDetails?.voicemailPreference || "",
  });

  // ─── Draft-save on "I'm here" ─────────────────────────────────
  const saveToContext = useCallback(() => {
    setData("dob", formData.dob || undefined);
    setData("memberDetails", {
      ...(data.memberDetails ?? {}),
      firstName: formData.memberFirstName || undefined,
      lastName: formData.memberLastName || undefined,
      genderIdentity: formData.memberGenderIdentity || undefined,
      preferredPronouns: formData.memberPreferredPronouns || undefined,
      language: formData.memberLanguage || undefined,
      streetAddress1: formData.memberStreetAddress1 || undefined,
      streetAddress2: formData.memberStreetAddress2 || undefined,
      city: formData.memberCity || undefined,
      state_code: formData.memberState || undefined,
      zip: formData.memberZipCode || undefined,
      email: formData.memberEmail || undefined,
      phone: formData.memberPhone || undefined,
      phoneType: formData.memberPhoneType || undefined,
      preferredContactMethod: formData.memberPreferredContactMethod || undefined,
      voicemailPreference: formData.memberLeaveVoicemailPreference || undefined,
      legalSex: formData.memberLegalSex || undefined,
    });
    setData(
      "isGuardianPolicyHolder",
      parentGuardianData.isPolicyHolder
        ? parentGuardianData.isPolicyHolder.toLowerCase() === "yes"
        : undefined,
    );
    setData("guardianDetails", {
      firstName: parentGuardianData.firstName || undefined,
      lastName: parentGuardianData.lastName || undefined,
      dob: parentGuardianData.dob || undefined,
      legalSex: parentGuardianData.legalSex || undefined,
      genderIdentity: parentGuardianData.genderIdentity || undefined,
      preferredPronouns: parentGuardianData.preferredPronouns || undefined,
      preferredLanguage: parentGuardianData.preferredLanguage || undefined,
      streetAddress1: parentGuardianData.streetAddress1 || undefined,
      streetAddress2: parentGuardianData.streetAddress2 || undefined,
      city: parentGuardianData.city || undefined,
      state: parentGuardianData.state || undefined,
      zipCode: parentGuardianData.zipCode || undefined,
      email: parentGuardianData.email || undefined,
      phone: parentGuardianData.phone || undefined,
      phoneType: parentGuardianData.phoneType || undefined,
      preferredContactMethod: parentGuardianData.preferredContactMethod || undefined,
      voicemailPreference: parentGuardianData.voicemailPreference || undefined,
    });
  }, [formData, parentGuardianData, data.memberDetails, setData]);

  useIdleFormFlush(saveToContext, { flushOnUnmount: true });

  // Patient search flow state
  const [isSearching, setIsSearching] = useState(false);
  const [isSavingData, setIsSavingData] = useState(false);
  const [isPatientFoundNavigatorModalOpen, setIsPatientFoundNavigatorModalOpen] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [isLegalSexReadOnly, setIsLegalSexReadOnly] = useState(false);
  const [sameAsPatientAddress, setSameAsPatientAddress] = useState(
    data.guardianSameAsPatientAddress ?? false,
  );
  // Stryker disable next-line BooleanLiteral
  const suppressContactUsRedirectRef = useRef(false);

  const [formErrors, setFormErrors] = useState({
    memberFirstName: "",
    memberLastName: "",
    dob: "",
    memberGenderIdentity: "",
    memberLegalSex: "",
    memberPreferredPronouns: "",
    memberLanguage: "",
    memberStreetAddress1: "",
    memberStreetAddress2: "",
    memberCity: "",
    memberState: "",
    memberZipCode: "",
    memberEmail: "",
    memberPhone: "",
    memberPhoneType: "",
    memberPreferredContactMethod: "",
    memberLeaveVoicemailPreference: "",
  });

  const [parentGuardianErrors, setParentGuardianErrors] = useState<ParentGuardianFormErrors>({});

  const selectedAgeRange = data.ageRange || "";
  const isMinor = isUnder18AgeRange(selectedAgeRange);
  const shouldShowParentGuardianSection = isMinor;
  const isGuardianPolicyHolderDisabled =
    shouldShowParentGuardianSection &&
    (data.paymentMethod === "self_pay" || data.isPrimaryPolicyHolder !== false);
  const isGuardianPolicyHolderHidden =
    shouldShowParentGuardianSection &&
    (data.paymentMethod === "self_pay" || data.isPrimaryPolicyHolder === true);

  useEffect(() => {
    if (!isGuardianPolicyHolderHidden) {
      return;
    }

    setParentGuardianData((previous) => {
      return previous.isPolicyHolder ? { ...previous, isPolicyHolder: "" } : previous;
    });
    setParentGuardianErrors((previous) => ({ ...previous, isPolicyHolder: undefined }));
    setData("isGuardianPolicyHolder", undefined);
  }, [isGuardianPolicyHolderHidden, setData]);

  const displayedParentGuardianData = isGuardianPolicyHolderDisabled
    ? {
        ...parentGuardianData,
        isPolicyHolder: "" as const,
        firstName:
          parentGuardianData.firstName === policyholderAutofillValues.firstName
            ? ""
            : parentGuardianData.firstName,
        lastName:
          parentGuardianData.lastName === policyholderAutofillValues.lastName
            ? ""
            : parentGuardianData.lastName,
        dob:
          parentGuardianData.dob === policyholderAutofillValues.dob ? "" : parentGuardianData.dob,
        legalSex:
          parentGuardianData.legalSex === policyholderAutofillValues.legalSex
            ? ""
            : parentGuardianData.legalSex,
      }
    : parentGuardianData;

  const labels = useResolvedPageLabels("yourInfoForm");

  const handleChange = (field, fieldValue) => {
    let formattedValue = fieldValue;

    // Format phone number to NANP format (XXX) XXX-XXXX
    if (field === "memberPhone") {
      // Remove all non-numeric characters
      const cleaned = fieldValue.replaceAll(/\D/g, "");

      // Apply formatting based on length
      if (cleaned.length <= 3) {
        formattedValue = cleaned;
      } else if (cleaned.length <= 6) {
        formattedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      } else {
        formattedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      }
    }

    if (field === "memberZipCode") {
      const rawValue = fieldValue.replaceAll(/\D/g, "");
      // Format as xxxxx or xxxxx-xxxx
      let zipValue = rawValue;
      if (rawValue.length > 5) {
        zipValue = `${rawValue.slice(0, 5)}-${rawValue.slice(5, 9)}`;
      }
      // Limit to 10 characters (including dash)
      if (zipValue.length > 10) {
        zipValue = zipValue.slice(0, 10);
      }
      formattedValue = zipValue;
    }
    if (field === "memberState") {
      const sanitizedValue = String(fieldValue)
        .replaceAll(/[^a-zA-Z]/g, "")
        .toUpperCase()
        .slice(0, 2);
      formattedValue = sanitizedValue;
    }

    setFormData({ ...formData, [field]: formattedValue });
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: "" });
    }
  };

  const handleParentGuardianChange = (field: keyof ParentGuardianFormData, fieldValue: string) => {
    let formattedValue = fieldValue;

    if (field === "phone") {
      const cleaned = fieldValue.replaceAll(/\D/g, "").slice(0, 10);
      if (cleaned.length <= 3) {
        formattedValue = cleaned;
      } else if (cleaned.length <= 6) {
        formattedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      } else {
        formattedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      }
    }

    if (field === "zipCode") {
      const rawValue = fieldValue.replaceAll(/\D/g, "");
      let zipValue = rawValue;
      if (rawValue.length > 5) {
        zipValue = `${rawValue.slice(0, 5)}-${rawValue.slice(5, 9)}`;
      }
      if (zipValue.length > 10) {
        zipValue = zipValue.slice(0, 10);
      }
      formattedValue = zipValue;
    }

    if (field === "state") {
      formattedValue = String(fieldValue)
        .replaceAll(/[^a-zA-Z]/g, "")
        .toUpperCase()
        .slice(0, 2);
    }

    if (field === "isPolicyHolder") {
      formattedValue = normalizeGuardianPolicyHolderInput(fieldValue);
    }

    setParentGuardianData((prev) => ({ ...prev, [field]: formattedValue }));
    setParentGuardianErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSameAsPatientAddressChange = useCallback(
    (checked: boolean) => {
      setSameAsPatientAddress(checked);
      setData("guardianSameAsPatientAddress", checked);
      if (checked) {
        setParentGuardianData((prev) => ({
          ...prev,
          streetAddress1: formData.memberStreetAddress1,
          streetAddress2: formData.memberStreetAddress2,
          city: formData.memberCity,
          state: formData.memberState,
          zipCode: formData.memberZipCode,
        }));
        setParentGuardianErrors((prev) => ({
          ...prev,
          streetAddress1: undefined,
          streetAddress2: undefined,
          city: undefined,
          state: undefined,
          zipCode: undefined,
        }));
      } else {
        setParentGuardianData((prev) => ({
          ...prev,
          streetAddress1: "",
          streetAddress2: "",
          city: "",
          state: "",
          zipCode: "",
        }));
      }
    },
    [
      formData.memberStreetAddress1,
      formData.memberStreetAddress2,
      formData.memberCity,
      formData.memberState,
      formData.memberZipCode,
    ],
  );

  const validateNameField = (value: string, emptyError: string): string | undefined => {
    return validateName(value, {
      requiredMessage: emptyError,
      invalidMessage: "Please enter a valid name",
    });
  };

  const validateAddressField = (
    value: string,
    emptyError: string,
    maxLength: number,
  ): string | undefined => {
    if (!value.trim()) {
      return emptyError;
    }
    if (value.length > maxLength) {
      return `Cannot be longer than ${maxLength} characters`;
    }
    return undefined;
  };

  const validateOptionalAddressField = (
    value: string,
    fieldLabel: string,
    maxLength: number,
  ): string | undefined => {
    if (value.length > maxLength) {
      return `${fieldLabel} cannot be longer than ${maxLength} characters`;
    }
    return undefined;
  };

  const validateZipField = (value: string): string | undefined => {
    if (!value) {
      return labels.errorZipCode;
    }
    if (!ZIP_VALIDATION_REGEX.test(value)) {
      return labels.errorZipCode;
    }
    return undefined;
  };

  const validateEmailField = (value: string): string | undefined => {
    if (!value) {
      const hasGuardianEmail =
        shouldShowParentGuardianSection && Boolean(parentGuardianData.email?.trim());
      if (hasGuardianEmail) {
        return undefined;
      }
      return labels.errorEmail;
    }
    if (value.length > 50) {
      return "Email cannot be longer than 50 characters";
    }
    if (!isValidEmailFormat(value)) {
      return "Please enter a valid email address";
    }
    return undefined;
  };

  const validatePhoneField = (value: string): string | undefined => {
    if (!value) {
      return labels.errorPhone;
    }
    if (!PHONE_VALIDATION_REGEX.test(value)) {
      return "Please enter a valid phone number";
    }
    return undefined;
  };

  const validateSelectField = (value: string, emptyError: string): string | undefined => {
    if (!value) {
      return emptyError;
    }
    return undefined;
  };

  const validateDobField = (value: string): string | undefined => {
    if (!value) {
      return labels.errorDob;
    }
    if (!isValidDateOfBirth(value)) {
      return "Please enter a valid date of birth";
    }
    return undefined;
  };

  const validateField = (field, value) => {
    switch (field) {
      case "memberFirstName":
        return validateNameField(value, labels.errorFirstName);
      case "memberLastName":
        return validateNameField(value, labels.errorLastName);
      case "memberStreetAddress1":
        return validateAddressField(value, labels.errorStreetAddress, 100);
      case "memberStreetAddress2":
        return validateOptionalAddressField(value, "Street address", 100);
      case "memberCity":
        return validateAddressField(value, labels.errorCity, 30);
      case "memberState":
        return value ? undefined : labels.errorState;
      case "memberZipCode":
        return validateZipField(value);
      case "memberEmail":
        return validateEmailField(value);
      case "memberPhone":
        return validatePhoneField(value);
      case "memberPhoneType":
        return validateSelectField(value, labels.errorPhoneType);
      case "memberLegalSex":
        return validateSelectField(value, labels.errorLegalSex);
      case "memberPreferredContactMethod":
        return validateSelectField(value, labels.errorContactMethod);
      case "memberLeaveVoicemailPreference":
        return validateSelectField(value, labels.errorVoicemailPreference);
      case "dob":
        return validateDobField(value);
      default:
        return undefined;
    }
  };

  const validateGuardianStringField = (
    value: string,
    fieldName: string,
    maxLength?: number,
  ): string | undefined => {
    if (fieldName === "first name" || fieldName === "last name") {
      return validateName(value, {
        requiredMessage: `Enter parent/guardian ${fieldName}`,
        invalidMessage: `Please enter a valid parent/guardian ${fieldName}`,
        maxLength,
      });
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return `Enter parent/guardian ${fieldName}`;
    }
    if (maxLength && trimmed.length > maxLength) {
      return `${fieldName} cannot be longer than ${maxLength} characters`;
    }
    return undefined;
  };

  const validateGuardianDobField = (value: string): string | undefined => {
    if (!value.trim()) {
      return "Enter parent/guardian date of birth";
    }
    if (!isValidDateOfBirth(value)) {
      return "Please enter a valid date of birth";
    }
    return undefined;
  };

  const validateGuardianSelectField = (value: string, fieldName: string): string | undefined => {
    if (!value) {
      return `Select parent/guardian ${fieldName}`;
    }
    return undefined;
  };

  const validateGuardianPolicyHolderField = (value: string): string | undefined => {
    if (value !== "yes" && value !== "no") {
      return "Select parent/guardian policy holder status";
    }

    return undefined;
  };

  const validateGuardianZipField = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) {
      return "Enter parent/guardian zip code";
    }
    if (!ZIP_VALIDATION_REGEX.test(trimmed)) {
      return "Please enter a valid parent/guardian zip code (5 digits or 5+4 format)";
    }
    return undefined;
  };

  const validateGuardianPhoneField = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) {
      return "Enter parent/guardian phone number";
    }
    if (!PHONE_VALIDATION_REGEX.test(trimmed)) {
      return "Please enter a valid phone number";
    }
    return undefined;
  };

  const validateGuardianEmailField = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) {
      return "Enter parent/guardian email";
    }
    if (!isValidEmailFormat(trimmed)) {
      return "Please enter a valid email address";
    }
    return undefined;
  };

  const validateParentGuardianForm = (
    guardianData: ParentGuardianFormData,
    isPolicyHolderDisabled: boolean,
  ): { errors: ParentGuardianFormErrors; isValid: boolean } => {
    const errors: ParentGuardianFormErrors = {};

    if (!isPolicyHolderDisabled) {
      errors.isPolicyHolder = validateGuardianPolicyHolderField(guardianData.isPolicyHolder);
    }
    errors.firstName = validateGuardianStringField(guardianData.firstName, "first name");
    errors.lastName = validateGuardianStringField(guardianData.lastName, "last name");
    errors.dob = validateGuardianDobField(guardianData.dob);
    errors.legalSex = validateGuardianSelectField(guardianData.legalSex, "legal sex");
    errors.streetAddress1 = validateGuardianStringField(
      guardianData.streetAddress1,
      "street address",
      100,
    );
    if (guardianData.streetAddress2.length > 100) {
      errors.streetAddress2 = "Street address cannot be longer than 100 characters";
    }
    errors.city = validateGuardianStringField(guardianData.city, "city", 30);
    errors.state = validateGuardianStringField(guardianData.state, "state", 2);
    errors.zipCode = validateGuardianZipField(guardianData.zipCode);
    errors.email = validateGuardianEmailField(guardianData.email);
    errors.phone = validateGuardianPhoneField(guardianData.phone);
    errors.phoneType = validateGuardianSelectField(guardianData.phoneType, "phone type");
    errors.preferredContactMethod = validateGuardianSelectField(
      guardianData.preferredContactMethod,
      "preferred contact method",
    );
    errors.voicemailPreference = validateGuardianSelectField(
      guardianData.voicemailPreference,
      "voicemail permission",
    );

    return {
      errors,
      isValid: Object.values(errors).every((value) => !value),
    };
  };

  const validateForm = () => {
    const newErrors = {
      memberFirstName: "",
      memberLastName: "",
      dob: "",
      memberGenderIdentity: "",
      memberLegalSex: "",
      memberPreferredPronouns: "",
      memberLanguage: "",
      memberStreetAddress1: "",
      memberStreetAddress2: "",
      memberCity: "",
      memberState: "",
      memberZipCode: "",
      memberEmail: "",
      memberPhone: "",
      memberPhoneType: "",
      memberPreferredContactMethod: "",
      memberLeaveVoicemailPreference: "",
    };
    Object.keys(formData).forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    setFormErrors(newErrors);

    let parentGuardianFormValid = true;
    if (shouldShowParentGuardianSection) {
      const parentGuardianValidation = validateParentGuardianForm(
        parentGuardianData,
        isGuardianPolicyHolderDisabled,
      );
      setParentGuardianErrors(parentGuardianValidation.errors);
      parentGuardianFormValid = parentGuardianValidation.isValid;
    }

    return Object.values(newErrors).every((v) => !v) && parentGuardianFormValid;
  };

  // Helper function to format DOB for backend API (YYYY-MM-DD)
  const formatDateForAPI = (dateString: string): string | undefined => {
    if (!dateString) return undefined;

    const mmddyyyyMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(dateString);
    if (mmddyyyyMatch) {
      const month = Number(mmddyyyyMatch[1]);
      const day = Number(mmddyyyyMatch[2]);
      const year = Number(mmddyyyyMatch[3]);
      const parsedDate = new Date(year, month - 1, day);

      if (
        parsedDate.getFullYear() === year &&
        parsedDate.getMonth() === month - 1 &&
        parsedDate.getDate() === day
      ) {
        return `${year.toString().padStart(4, "0")}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
      }

      return undefined;
    }

    const yyyymmddMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
    if (yyyymmddMatch) {
      const year = Number(yyyymmddMatch[1]);
      const month = Number(yyyymmddMatch[2]);
      const day = Number(yyyymmddMatch[3]);
      const parsedDate = new Date(year, month - 1, day);

      if (
        parsedDate.getFullYear() === year &&
        parsedDate.getMonth() === month - 1 &&
        parsedDate.getDate() === day
      ) {
        return `${year.toString().padStart(4, "0")}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
      }

      return undefined;
    }

    const parsedDate = new Date(dateString);
    if (Number.isNaN(parsedDate.getTime())) {
      return undefined;
    }

    const month = (parsedDate.getMonth() + 1).toString().padStart(2, "0");
    const day = parsedDate.getDate().toString().padStart(2, "0");
    const year = parsedDate.getFullYear().toString().padStart(4, "0");
    return `${year}-${month}-${day}`;
  };

  // Helper function to format DOB for patient search (MM/DD/YYYY)
  const formatDateForSearch = (dateString: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Function to perform patient search with retry logic
  const performPatientSearch = async (): Promise<boolean> => {
    try {
      setSearchError("");
      const patientSearchEmail = formData.memberEmail?.trim();

      const searchResult = await searchPatients({
        user_uuid: data.userUuid || "",
        first_name: formData.memberFirstName,
        last_name: formData.memberLastName,
        dob: formatDateForSearch(formData.dob),
        zipcode: data.zip || "",
        ...(patientSearchEmail ? { email: patientSearchEmail } : {}),
        practice_id: data.address?.context_id || 0,
      });

      if (searchResult.success) {
        // Check if patients were found
        return searchResult.data?.foundPatient ?? false;
      } else {
        throw new Error(searchResult.error || "Patient search failed");
      }
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Unknown error during patient search",
      );
    }
  };

  // Function to save patient data
  const savePatientDataToBackend = async (isUserUnder18 = false): Promise<boolean> => {
    try {
      const formattedPatientDob = formatDateForAPI(formData.dob);
      const formattedSubscriberDob = data.isPrimaryPolicyHolder
        ? formattedPatientDob
        : formatDateForAPI(data.policyholderDOB || "");
      const formattedGuardianDob = shouldShowParentGuardianSection
        ? formatDateForAPI(parentGuardianData.dob)
        : undefined;
      const requiresSubscriberDob = data.paymentMethod !== "self_pay";

      if (
        !formattedPatientDob ||
        (requiresSubscriberDob && !formattedSubscriberDob) ||
        (shouldShowParentGuardianSection && !formattedGuardianDob)
      ) {
        ClientLogsService.logUserError(
          data.userUuid || "unknown",
          "YourInfoForm.savePatientDataToBackend",
          "Invalid required DOB encountered while preparing patient payload",
        );
        setSearchError("Invalid date of birth in patient data. Please review and try again.");
        return false;
      }

      const appointmentDateTime =
        toAppointmentIsoDateTime(
          data.appointmentDetails?.apptDate,
          data.appointmentDetails?.apptTime,
        ) ?? new Date().toISOString();

      const patientDataPayload = {
        patient_first_name: formData.memberFirstName,
        patient_last_name: formData.memberLastName,
        patient_dob: formattedPatientDob,
        appointment_datetime: appointmentDateTime,
        provider_id: Number.parseInt(data.selectedProvider?.providerId || "0", 10),
        provider_clinic: data.appointmentDetails?.apptLocation?.department_id || 0,
        insurance_member_id: data.memberId || "",
        insurance_package_id: data.insurancePolicy?.id
          ? Number.parseInt(data.insurancePolicy.id, 10)
          : 0,
        // isPrimaryPolicyHolder is authoritative; a stale non-primary policyholder value must not leak in
        insurance_policy_subscriber_first_name: data.isPrimaryPolicyHolder
          ? formData.memberFirstName
          : data.policyholderFirstName,
        insurance_policy_subscriber_last_name: data.isPrimaryPolicyHolder
          ? formData.memberLastName
          : data.policyholderLastName,
        // insurance_relationship_to_policy_holder: 1, // Default to self ... aleady saved, no need to pass again.
        insurance_policy_subscriber_dob: formattedSubscriberDob,
        legal_sex: formData.memberLegalSex,
        ...(formData.memberEmail ? { patient_email: formData.memberEmail } : {}),
        attested_terms_privacy: Boolean(data.termsChecked),
        consent_to_contact: Boolean(data.communicationChecked),
        ...(shouldShowParentGuardianSection
          ? {
              guardian: {
                first_name: parentGuardianData.firstName.trim(),
                last_name: parentGuardianData.lastName.trim(),
                // formattedGuardianDob is guaranteed to be a string here: the early-return guard
                // above ensures it is defined whenever shouldShowParentGuardianSection is true.
                dob: formattedGuardianDob!,
                legal_sex: parentGuardianData.legalSex,
                gender_identity: parentGuardianData.genderIdentity || undefined,
                preferred_pronouns: parentGuardianData.preferredPronouns || undefined,
                preferred_language: parentGuardianData.preferredLanguage || undefined,
                address1: parentGuardianData.streetAddress1.trim(),
                address2: parentGuardianData.streetAddress2.trim() || undefined,
                city: parentGuardianData.city.trim(),
                state: parentGuardianData.state.trim(),
                zip: parentGuardianData.zipCode.trim(),
                email: parentGuardianData.email.trim(),
                phone: parentGuardianData.phone,
                phone_type: parentGuardianData.phoneType,
                preferred_contact: parentGuardianData.preferredContactMethod,
                ok_to_leave_vm: parentGuardianData.voicemailPreference?.toLowerCase() === "yes",
                is_policy_holder: parentGuardianData.isPolicyHolder
                  ? parentGuardianData.isPolicyHolder?.toLowerCase() === "yes"
                  : undefined,
              },
            }
          : {}),
        utm_source: utmTags?.utm_source ?? null,
        utm_medium: utmTags?.utm_medium ?? null,
        utm_campaign: utmTags?.utm_campaign ?? null,
        utm_content: utmTags?.utm_content ?? null,
        utm_theme: utmTags?.utm_theme ?? null,
        practice_id: data.address?.context_id || 0,
        signup_as_minor: isUserUnder18,
        ...(data.medMgmtInd ? { med_mgmt_ind: 1 } : {}),
      };

      // Self-pay has no insurance; strip all insurance fields before the call regardless of stale context values
      const {
        insurance_member_id,
        insurance_package_id,
        insurance_policy_subscriber_first_name,
        insurance_policy_subscriber_last_name,
        insurance_policy_subscriber_dob,
        ...selfPayPatientDataPayload
      } = patientDataPayload;
      const sanitizedPatientDataPayload =
        data.paymentMethod === "self_pay" ? selfPayPatientDataPayload : patientDataPayload;

      const saveResult = await PatientDataService.savePatientData(
        data.userUuid || "",
        sanitizedPatientDataPayload,
      );

      return saveResult.message ?? false;
    } catch {
      return false;
    }
  };

  const scheduleContactUsRedirect = useCallback(() => {
    // Stryker disable next-line ConditionalExpression
    if (navTimeoutRef.current) {
      clearTimeout(navTimeoutRef.current);
    }
    navTimeoutRef.current = globalThis.setTimeout(() => {
      navigateToContactUs(navigate, data.userUuid);
    }, 5000);
    // Stryker disable next-line ArrayDeclaration
  }, [navigate, data.userUuid]);

  const buildSubmittedMemberDetails = () => ({
    ...data.memberDetails,
    city: formData.memberCity,
    state_code: formData.memberState,
    zip: formData.memberZipCode,
    streetAddress1: formData.memberStreetAddress1,
    streetAddress2: formData.memberStreetAddress2,
    firstName: formData.memberFirstName,
    lastName: formData.memberLastName,
    email: formData.memberEmail,
    phone: formData.memberPhone,
    language: formData.memberLanguage,
  });

  const persistGuardianDataToContext = () => {
    setData(
      "isGuardianPolicyHolder",
      parentGuardianData.isPolicyHolder
        ? // Stryker disable next-line OptionalChaining
          parentGuardianData.isPolicyHolder?.toLowerCase() === "yes"
        : undefined,
    );
    setData("guardianDetails", {
      firstName: parentGuardianData.firstName,
      lastName: parentGuardianData.lastName,
      dob: parentGuardianData.dob,
      legalSex: parentGuardianData.legalSex,
      genderIdentity: parentGuardianData.genderIdentity,
      preferredPronouns: parentGuardianData.preferredPronouns,
      preferredLanguage: parentGuardianData.preferredLanguage,
      streetAddress1: parentGuardianData.streetAddress1,
      streetAddress2: parentGuardianData.streetAddress2,
      city: parentGuardianData.city,
      state: parentGuardianData.state,
      zipCode: parentGuardianData.zipCode,
      email: parentGuardianData.email,
      phone: parentGuardianData.phone,
      phoneType: parentGuardianData.phoneType,
      preferredContactMethod: parentGuardianData.preferredContactMethod,
      voicemailPreference: parentGuardianData.voicemailPreference,
    });
  };

  const handleSubmit = useCallback(async (): Promise<FormDetails> => {
    let formInfo: FormDetails;
    suppressContactUsRedirectRef.current = false;
    if (navTimeoutRef.current) {
      clearTimeout(navTimeoutRef.current);
      navTimeoutRef.current = null;
    }

    if (shouldShowParentGuardianSection) {
      persistGuardianDataToContext();
    }

    // Validate both the form fields AND the footer checkboxes
    const formIsValid = validateForm();
    const checkboxesAreValid = validateFooterCheckboxes();

    if (!formIsValid || !checkboxesAreValid) {
      ClientLogsService.logUserError(
        data?.userUuid || "unknown",
        "YourInfoForm.handleSubmit",
        "Form has validation errors, unable to proceed",
      );
      formInfo = { isCompleted: false };
      setData("yourInfoForm", formInfo);
      return formInfo;
    }

    const patientDob = validateDateOfBirth(formData.dob);
    const hasAgeRangeMismatch =
      patientDob &&
      evaluateAgeModalScenario(formData.dob, {
        selectedAgeRange,
      }).scenario === "age_range_mismatch";
    if (hasAgeRangeMismatch) {
      return {
        isCompleted: true,
        memberDOB: patientDob,
      };
    }

    ClientLogsService.logWebAction(
      data.userUuid || "unknown",
      "YourInfoForm.handleSubmit",
      "Form validation passed - proceeding to check if patient exists",
      undefined,
      {},
    );

    // Save form data to context first
    setData("dob", formData.dob);
    setData("memberDetails", {
      ...data.memberDetails,
      city: formData.memberCity,
      state_code: formData.memberState,
      zip: formData.memberZipCode,
      streetAddress1: formData.memberStreetAddress1,
      streetAddress2: formData.memberStreetAddress2,
      firstName: formData.memberFirstName,
      lastName: formData.memberLastName,
      genderIdentity: formData.memberGenderIdentity,
      preferredPronouns: formData.memberPreferredPronouns,
      language: formData.memberLanguage,
      email: formData.memberEmail,
      phone: formData.memberPhone,
      phoneType: formData.memberPhoneType,
      preferredContactMethod: formData.memberPreferredContactMethod,
      voicemailPreference: formData.memberLeaveVoicemailPreference,
      legalSex: formData.memberLegalSex,
    });

    // Compute under-18 status deterministically from current DOB value
    const dobValue = patientDob;
    const dobLocal = parseDateForCalendarComparison(formData.dob);
    const { twelveCutoff } = getAgeCutoffs();
    const isUserUnder18 = isMinor;
    const isUserUnder12 = dobLocal ? dobLocal > twelveCutoff : false;

    const inPersonSessionType = Array.isArray(data.sessionType) ? ["In-person"] : "In-person";
    setData("isUnder12InPersonProceed", isUserUnder12);
    if (isUserUnder12) {
      setData("sessionType", inPersonSessionType);
    }

    // Start patient search flow asynchronously - form completion will be set based on search results
    const isFormComplete = await performPatientSearchFlow(isUserUnder18);

    if (!isFormComplete) {
      formInfo = { isCompleted: false };
      setData("yourInfoForm", formInfo);
      if (!suppressContactUsRedirectRef.current) {
        scheduleContactUsRedirect();
      }
      return formInfo;
    }

    const snapshotBase = buildSubmittedDataSnapshot(data);
    formInfo = {
      isCompleted: true,
      isUnder18: isUserUnder18,
      memberDOB: dobValue,
      submittedData: {
        ...snapshotBase,
        dob: formData.dob,
        memberDetails: buildSubmittedMemberDetails(),
      },
    };
    setData("yourInfoForm", formInfo);
    return formInfo;
  }, [
    formData,
    isMinor,
    parentGuardianData,
    shouldShowParentGuardianSection,
    data.termsChecked,
    data.communicationChecked,
    data.footerTermsCheckboxError,
    data.footerCommunicationCheckboxError,
    validateFooterCheckboxes,
    scheduleContactUsRedirect,
    setData,
  ]);

  // Helper to handle patient found scenario
  const handlePatientFoundScenario = async (isUserUnder18: boolean): Promise<boolean> => {
    setIsSavingData(true);
    const isPatientNavigator = canRoleBypassGuardrails(data.userRole);
    ClientLogsService.logWebAction(
      data.userUuid || "unknown",
      "YourInfoForm.performPatientSearchFlow",
      isPatientNavigator
        ? "Patient found during search - showing patient navigator modal"
        : "Patient found during search - redirecting to Contact Us",
      undefined,
      {},
    );

    if (isPatientNavigator && data.userUuid) {
      try {
        await unfreezeAppointment(data.userUuid);
        ClientLogsService.logWebAction(
          data.userUuid,
          "YourInfoForm.performPatientSearchFlow",
          "Patient navigator flow: unfroze appointment after patient match.",
        );
      } catch (error) {
        ClientLogsService.logError(
          data.userUuid,
          "YourInfoForm.performPatientSearchFlow",
          "Failed to unfreeze appointment for patient navigator flow.",
          error instanceof Error ? error : new Error("Unknown error"),
        );
      }
    }

    await savePatientDataToBackend(isUserUnder18);
    setIsSavingData(false);

    if (isPatientNavigator) {
      suppressContactUsRedirectRef.current = true;
      setIsPatientFoundNavigatorModalOpen(true);
    }

    return false;
  };

  const handlePatientNavigatorModalOk = () => {
    setIsPatientFoundNavigatorModalOpen(false);
    navigate(APP_ROUTES.GET_STARTED);
  };

  // Helper to handle patient not found scenario
  const handlePatientNotFoundScenario = async (
    isUserUnder18: boolean,
  ): Promise<boolean | undefined> => {
    setIsSavingData(true);
    ClientLogsService.logWebAction(
      data.userUuid || "unknown",
      "YourInfoForm.performPatientSearchFlow",
      "Patient not found - saving patient data and proceeding",
      undefined,
      {},
    );
    const saveSuccess = await savePatientDataToBackend(isUserUnder18);
    setIsSavingData(false);

    if (saveSuccess) {
      return true;
    } else {
      ClientLogsService.logError(
        data.userUuid || "unknown",
        "YourInfoForm.performPatientSearchFlow",
        "Failed to save patient data.",
      );
      setSearchError((prev) => prev || "Failed to save patient data. Please try again.");
      return false;
    }
  };

  // Helper to handle max attempts failure
  const handleMaxAttemptsExceeded = async (
    isUserUnder18: boolean,
    error: unknown,
  ): Promise<void> => {
    ClientLogsService.logError(
      data.userUuid || "unknown",
      "YourInfoForm.performPatientSearchFlow",
      "Patient search flow failed after multiple attempts.",
      error instanceof Error ? error : new Error("Unknown error"),
    );

    setIsSearching(false);
    setIsSavingData(true);
    await savePatientDataToBackend(isUserUnder18);
    setIsSavingData(false);
    setSearchError(
      "Unable to process your request after multiple attempts. Please contact us for assistance.",
    );

    CommonToast({
      title: "Something went wrong",
      message: (
        <Fragment>
          An unexpected error occurred. Try again, or contact us at{" "}
          <strong>
            <InvocaPhoneNumber />
          </strong>
          .
        </Fragment>
      ),
      toastType: "error",
      toastIcon: "error",
      withCloseButton: true,
      onClose: () => {
        ClientLogsService.logWebAction(
          data.userUuid || "unknown",
          "YourInfoForm.CommonToast",
          "Your Info Form Common Toast has closed - Redirecting to Contact Us",
        );
        if (navTimeoutRef.current) {
          clearTimeout(navTimeoutRef.current);
        }
        navigateToContactUs(navigate, data.userUuid);
      },
    });

    navTimeoutRef.current = globalThis.setTimeout(() => {
      navigateToContactUs(navigate, data.userUuid);
    }, 5000);
  };

  // Async function to handle the complete patient search flow
  const performPatientSearchFlow = async (isUserUnder18: boolean): Promise<boolean | undefined> => {
    setIsSearching(true);
    setSearchError("");
    let currentAttempt = 0;
    const maxAttempts = 3;

    while (currentAttempt < maxAttempts) {
      try {
        const patientFound = await performPatientSearch();
        setIsSearching(false);

        if (patientFound) {
          return await handlePatientFoundScenario(isUserUnder18);
        } else {
          return await handlePatientNotFoundScenario(isUserUnder18);
        }
      } catch (error) {
        currentAttempt += 1;

        if (currentAttempt >= maxAttempts) {
          await handleMaxAttemptsExceeded(isUserUnder18, error);
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }

    return false;
  };

  const handleFormSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault(); // Prevent default form submission behavior
  };

  useCanvasPrefillSync(setFormData, {
    memberFirstName: (d) => d.memberDetails?.firstName,
    memberLastName: (d) => d.memberDetails?.lastName,
    memberGenderIdentity: (d) => d.memberDetails?.genderIdentity,
    memberLegalSex: (d) => d.gender || d.memberDetails?.legalSex,
    memberPreferredPronouns: (d) => d.memberDetails?.preferredPronouns,
    memberLanguage: (d) => d.memberDetails?.language,
    memberStreetAddress1: (d) => d.memberDetails?.streetAddress1,
    memberStreetAddress2: (d) => d.memberDetails?.streetAddress2,
    memberCity: (d) => d.memberDetails?.city || d.address?.city,
    memberState: (d) => d.memberDetails?.state_code || d.address?.state_code,
    memberZipCode: (d) => d.memberDetails?.zip || d.zip,
    memberEmail: (d) => d.memberDetails?.email,
    memberPhone: (d) => d.memberDetails?.phone,
    memberPhoneType: (d) => d.memberDetails?.phoneType,
    memberPreferredContactMethod: (d) => d.memberDetails?.preferredContactMethod,
    memberLeaveVoicemailPreference: (d) => d.memberDetails?.voicemailPreference,
  });

  useEffect(() => {
    // Clear footer checkbox error states on mount so they don't persist
    // when the user navigates back and returns to this step.
    setData("footerTermsCheckboxError", false);
    setData("footerCommunicationCheckboxError", false);

    return () => {
      if (navTimeoutRef.current !== null) {
        clearTimeout(navTimeoutRef.current);
      }
    };
  }, []);

  // Auto-populate legal sex from gender in ApplicationContext
  useEffect(() => {
    // If patient address becomes incomplete while checkbox is checked, uncheck and clear guardian address
    const isComplete = Boolean(
      formData.memberStreetAddress1 &&
        formData.memberCity &&
        formData.memberState &&
        formData.memberZipCode,
    );
    if (!isComplete && sameAsPatientAddress) {
      setSameAsPatientAddress(false);
      setData("guardianSameAsPatientAddress", false);
      setParentGuardianData((prev) => ({
        ...prev,
        streetAddress1: "",
        streetAddress2: "",
        city: "",
        state: "",
        zipCode: "",
      }));
    }
  }, [
    formData.memberStreetAddress1,
    formData.memberCity,
    formData.memberState,
    formData.memberZipCode,
  ]);

  // Auto-populate legal sex from gender in ApplicationContext
  useEffect(() => {
    if (data.gender) {
      // gender is stored as 'M' or 'F', use directly as legal sex
      setFormData((prev) => ({
        ...prev,
        memberLegalSex: data.gender ?? "",
      }));
      setIsLegalSexReadOnly(true);
    } else {
      setIsLegalSexReadOnly(false);
    }
  }, [data.gender]);

  useEffect(() => {
    setHandleSubmit(() => handleSubmit);
  }, [handleSubmit, setHandleSubmit]);

  useEffect(() => {
    if (!shouldShowParentGuardianSection) {
      return;
    }

    const previousSelection = previousGuardianPolicyHolderRef.current;
    const currentSelection = parentGuardianData.isPolicyHolder;

    if (previousSelection?.toLowerCase() === "yes" && currentSelection?.toLowerCase() === "no") {
      const {
        firstName: autoFilledFirstName,
        lastName: autoFilledLastName,
        dob: autoFilledDob,
        legalSex: autoFilledLegalSex,
      } = guardianNameAutofillRef.current;
      const shouldClearKnownAutofill =
        guardianNameAutofillRef.current.wasAutofilled || data.isGuardianPolicyHolder === true;

      if (shouldClearKnownAutofill) {
        const shouldClearFirstName =
          (Boolean(autoFilledFirstName) && parentGuardianData.firstName === autoFilledFirstName) ||
          (Boolean(policyholderAutofillValues.firstName) &&
            parentGuardianData.firstName === policyholderAutofillValues.firstName);
        const shouldClearLastName =
          (Boolean(autoFilledLastName) && parentGuardianData.lastName === autoFilledLastName) ||
          (Boolean(policyholderAutofillValues.lastName) &&
            parentGuardianData.lastName === policyholderAutofillValues.lastName);
        const shouldClearDob =
          (Boolean(autoFilledDob) && parentGuardianData.dob === autoFilledDob) ||
          (Boolean(policyholderAutofillValues.dob) &&
            parentGuardianData.dob === policyholderAutofillValues.dob);
        const shouldClearLegalSex =
          (Boolean(autoFilledLegalSex) && parentGuardianData.legalSex === autoFilledLegalSex) ||
          (Boolean(policyholderAutofillValues.legalSex) &&
            parentGuardianData.legalSex === policyholderAutofillValues.legalSex);

        if (shouldClearFirstName || shouldClearLastName || shouldClearDob || shouldClearLegalSex) {
          setParentGuardianData((prev) => ({
            ...prev,
            firstName: resolveGuardianTransitionValue(
              data.isPrimaryPolicyHolder === false,
              guardianAutofillValues.firstName,
              shouldClearFirstName,
              prev.firstName,
            ),
            lastName: resolveGuardianTransitionValue(
              data.isPrimaryPolicyHolder === false,
              guardianAutofillValues.lastName,
              shouldClearLastName,
              prev.lastName,
            ),
            dob: resolveGuardianTransitionValue(
              data.isPrimaryPolicyHolder === false,
              guardianAutofillValues.dob,
              shouldClearDob,
              prev.dob,
            ),
            legalSex: resolveGuardianTransitionValue(
              data.isPrimaryPolicyHolder === false,
              guardianAutofillValues.legalSex,
              shouldClearLegalSex,
              prev.legalSex,
            ),
          }));

          setParentGuardianErrors((prev) => ({
            ...prev,
            ...(shouldClearFirstName ? { firstName: undefined } : {}),
            ...(shouldClearLastName ? { lastName: undefined } : {}),
            ...(shouldClearDob ? { dob: undefined } : {}),
            ...(shouldClearLegalSex ? { legalSex: undefined } : {}),
          }));
        }
      }

      guardianNameAutofillRef.current = {
        wasAutofilled: false,
        firstName: "",
        lastName: "",
        dob: "",
        legalSex: "",
      };
    }

    if (isGuardianPolicyHolderDisabled) {
      previousGuardianPolicyHolderRef.current = currentSelection;
      return;
    }

    if (parentGuardianData.isPolicyHolder?.toLowerCase() === "yes") {
      const {
        firstName: policyholderFirstName,
        lastName: policyholderLastName,
        dob: policyholderDob,
        legalSex: policyholderLegalSex,
      } = policyholderAutofillValues;
      const hasPolicyholderName = Boolean(policyholderFirstName || policyholderLastName);

      if (!hasPolicyholderName) {
        previousGuardianPolicyHolderRef.current = currentSelection;
        return;
      }

      setParentGuardianData((prev) => {
        const nextGuardianData = {
          ...prev,
          firstName: policyholderFirstName || prev.firstName,
          lastName: policyholderLastName || prev.lastName,
          dob: policyholderDob || prev.dob,
          legalSex: policyholderLegalSex || prev.legalSex,
        };

        const hasUpdates =
          nextGuardianData.firstName !== prev.firstName ||
          nextGuardianData.lastName !== prev.lastName ||
          nextGuardianData.dob !== prev.dob ||
          nextGuardianData.legalSex !== prev.legalSex;

        guardianNameAutofillRef.current = {
          wasAutofilled: true,
          firstName: policyholderFirstName,
          lastName: policyholderLastName,
          dob: policyholderDob,
          legalSex: policyholderLegalSex,
        };

        return hasUpdates ? nextGuardianData : prev;
      });

      setParentGuardianErrors((prev) => ({
        ...prev,
        firstName: undefined,
        lastName: undefined,
        ...(policyholderAutofillValues.dob ? { dob: undefined } : {}),
        ...(policyholderAutofillValues.legalSex ? { legalSex: undefined } : {}),
      }));
    } else if (data.isPrimaryPolicyHolder === false) {
      const hasGuardianDetails = Boolean(
        guardianAutofillValues.firstName ||
          guardianAutofillValues.lastName ||
          guardianAutofillValues.dob ||
          guardianAutofillValues.legalSex,
      );

      if (hasGuardianDetails) {
        setParentGuardianData((prev) => ({
          ...prev,
          firstName: guardianAutofillValues.firstName || prev.firstName,
          lastName: guardianAutofillValues.lastName || prev.lastName,
          dob: guardianAutofillValues.dob || prev.dob,
          legalSex: guardianAutofillValues.legalSex || prev.legalSex,
        }));

        setParentGuardianErrors((prev) => ({
          ...prev,
          firstName: undefined,
          lastName: undefined,
          ...(guardianAutofillValues.dob ? { dob: undefined } : {}),
          ...(guardianAutofillValues.legalSex ? { legalSex: undefined } : {}),
        }));
      }
    }

    previousGuardianPolicyHolderRef.current = currentSelection;
  }, [
    shouldShowParentGuardianSection,
    isGuardianPolicyHolderDisabled,
    parentGuardianData.isPolicyHolder,
    data.isGuardianPolicyHolder,
    data.isPrimaryPolicyHolder,
    guardianAutofillValues,
    policyholderAutofillValues,
  ]);

  return (
    <Fragment>
      {/* Loading Overlay for patient search and data saving */}
      <LoadingOverlay isLoading={isSearching || isSavingData} />
      <PatientFoundNavigatorModal
        isOpen={isPatientFoundNavigatorModalOpen}
        onClose={() => {
          setIsPatientFoundNavigatorModalOpen(false);
        }}
        onOk={handlePatientNavigatorModalOk}
        selectedProvider={data.selectedProvider}
        appointmentDetails={data.appointmentDetails}
        patientFirstName={formData.memberFirstName}
        patientLastName={formData.memberLastName}
      />
      <FormCard
        data-testid="test-id-your-info-form-card"
        className={isMobile ? "v2card-form-mobile-style" : ""}
      >
        {/* Error message display */}
        {searchError && (
          <SearchErrorBanner data-testid="test-id-your-info-search-error-banner">
            {searchError}
          </SearchErrorBanner>
        )}

        <form
          data-testid="test-id-your-info-member-info-form"
          id={patientInfoFormIds.YOUR_INFO}
          onSubmit={handleFormSubmit}
        >
          <FormSpacing data-testid="test-id-your-info-form-spacing">
            <Heading size="xs" data-testid="test-id-your-info-section-heading-patient">
              Patient
            </Heading>
            <FormRow isMobile={isMobile} data-testid="test-id-your-info-name-row">
              <TextInput
                data-testid="test-id-your-info-member-first-name-input"
                label="First name"
                value={formData.memberFirstName}
                onChange={(e) => handleChange("memberFirstName", e.target.value)}
                isRequired
                errorMessage={formErrors.memberFirstName}
                css={{ flex: 1 }}
                aria-label={labels.ariaFirstName}
              />
              <TextInput
                data-testid="test-id-your-info-member-last-name-input"
                label="Last name"
                value={formData.memberLastName}
                onChange={(e) => handleChange("memberLastName", e.target.value)}
                isRequired
                errorMessage={formErrors.memberLastName}
                css={{ flex: 1 }}
                aria-label={labels.ariaLastName}
              />
            </FormRow>
            <FormRowTopAligned
              isMobile={isMobile}
              data-testid="test-id-your-info-dob-legal-sex-row"
            >
              <DobInputWithAgeRangeTooltip>
                <StyledDateInput
                  data-testid="test-id-your-info-member-dob-input"
                  isRequired
                  label={
                    (
                      <DobLabelWithAgeRangeTooltip>
                        {labels.labelDob}
                        <DobRequiredIndicator aria-hidden="true">*</DobRequiredIndicator>
                        {selectedAgeRange && (
                          <Tooltip
                            content={`Selected age range: ${selectedAgeRange}`}
                            aria-label="Age range and date of birth guidance"
                            data-testid="test-id-your-info-age-range-guidance"
                          >
                            <DobGuidanceIcon>
                              <IconSymbol icon="info" size="16" />
                            </DobGuidanceIcon>
                          </Tooltip>
                        )}
                      </DobLabelWithAgeRangeTooltip>
                    ) as unknown as string
                  }
                  value={formData.dob || ""}
                  maxDate={dayjs(new Date(Date.now() - ONE_DAY_IN_MS))}
                  onChange={(e) => {
                    const dobValue =
                      typeof e === "string"
                        ? e
                        : (e as ChangeEvent<HTMLInputElement>)?.target?.value;
                    handleChange("dob", dobValue || "");
                  }}
                  errorMessage={formErrors.dob}
                  aria-label={labels.ariaDob}
                />
              </DobInputWithAgeRangeTooltip>
              {isLegalSexReadOnly ? (
                <TextInput
                  data-testid="test-id-your-info-member-legal-sex-input-disabled"
                  label="Legal sex"
                  value={formData.memberLegalSex?.toLowerCase() === "m" ? "Male" : "Female"}
                  isDisabled
                  isRequired
                  errorMessage={formErrors.memberLegalSex}
                  aria-label={labels.ariaLegalSex}
                />
              ) : (
                <SelectInput
                  enableOutsideScroll={canvasIsInSalesforce}
                  data-testid="test-id-your-info-member-legal-sex-input"
                  label="Legal sex"
                  value={formData.memberLegalSex}
                  onChange={(e) => handleChange("memberLegalSex", e)}
                  isRequired
                  errorMessage={formErrors.memberLegalSex}
                  options={LegalSexOptions}
                  aria-label={labels.ariaLegalSex}
                />
              )}
            </FormRowTopAligned>
            <FormRow isMobile={isMobile} data-testid="test-id-your-info-gender-pronouns-row">
              <SelectInput
                enableOutsideScroll={canvasIsInSalesforce}
                data-testid="test-id-your-info-member-gender-identity-input"
                label="Gender identity (optional)"
                value={formData.memberGenderIdentity}
                onChange={(e) => handleChange("memberGenderIdentity", e)}
                isRequired={false}
                errorMessage={formErrors.memberGenderIdentity}
                options={GenderIdentityOptions}
                css={{ flex: 1 }}
                aria-label={labels.ariaGenderIdentity}
              />
              <SelectInput
                enableOutsideScroll={canvasIsInSalesforce}
                data-testid="test-id-your-info-member-preferred-pronouns-input"
                label="Preferred pronouns (optional)"
                value={formData.memberPreferredPronouns}
                onChange={(e) => handleChange("memberPreferredPronouns", e)}
                isRequired={false}
                errorMessage={formErrors.memberPreferredPronouns}
                options={PreferredPronounsOptions}
                css={{ flex: 1 }}
                aria-label={labels.ariaPreferredPronouns}
              />
            </FormRow>
            <SelectInput
              enableOutsideScroll={canvasIsInSalesforce}
              data-testid="test-id-your-info-member-language-input"
              label={labels.labelLanguage}
              value={formData.memberLanguage}
              onChange={(e) => handleChange("memberLanguage", e)}
              isRequired={false}
              errorMessage={formErrors.memberLanguage}
              options={LanguagesOptions.map((lang) => ({
                value: lang,
                label: lang,
              }))}
              aria-label={labels.ariaLanguage}
            />
            <TextInput
              data-testid="test-id-your-info-member-street-address-input"
              label="Street address"
              value={formData.memberStreetAddress1}
              onChange={(e) => handleChange("memberStreetAddress1", e.target.value)}
              isRequired
              errorMessage={formErrors.memberStreetAddress1}
              aria-label={labels.ariaStreetAddress}
            />
            <TextInput
              data-testid="test-id-your-info-member-street-address-2-input"
              label="Street address 2 (optional)"
              value={formData.memberStreetAddress2}
              onChange={(e) => handleChange("memberStreetAddress2", e.target.value)}
              isRequired={false}
              errorMessage={formErrors.memberStreetAddress2}
              aria-label={labels.ariaStreetAddress2}
            />
            <FormRow isMobile={isMobile} data-testid="test-id-your-info-city-state-zip-row">
              <TextInput
                data-testid="test-id-your-info-member-city-input"
                label="City"
                value={formData.memberCity}
                onChange={(e) => handleChange("memberCity", e.target.value)}
                isRequired
                errorMessage={formErrors.memberCity}
                css={{ flex: 1 }}
                aria-label={labels.ariaCity}
              />
              <TextInput
                data-testid="test-id-your-info-member-state-input"
                label="State"
                value={formData.memberState}
                onChange={(e) => handleChange("memberState", e.target.value)}
                isRequired
                errorMessage={formErrors.memberState}
                css={{ flex: 1 }}
                aria-label={labels.ariaState}
              />
              <TextInput
                data-testid="test-id-your-info-member-zip-code-input"
                label="Zip code"
                value={formData.memberZipCode}
                onChange={(e) => handleChange("memberZipCode", e.target.value)}
                isRequired
                errorMessage={formErrors.memberZipCode}
                css={{ flex: 1 }}
                aria-label={labels.ariaZipCode}
              />
            </FormRow>
            <TextInput
              data-testid="test-id-your-info-member-email-input"
              label={labels.labelEmail}
              value={formData.memberEmail}
              onChange={(e) => handleChange("memberEmail", e.target.value)}
              isRequired
              placeholder="name@email.com"
              errorMessage={formErrors.memberEmail}
              aria-label={labels.ariaEmail}
            />
            <FormRow isMobile={isMobile} data-testid="test-id-your-info-phone-row">
              <TextInput
                data-testid="test-id-your-info-member-phone-input"
                label={labels.labelPhone}
                value={formData.memberPhone}
                onChange={(e) => handleChange("memberPhone", e.target.value)}
                isRequired
                placeholder="(223) 456-7890"
                errorMessage={formErrors.memberPhone}
                maxLength={MAX_PHONE_NUMBER_LENGTH}
                css={{ flex: 1 }}
                aria-label={labels.ariaPhone}
              />
              <SelectInput
                enableOutsideScroll={canvasIsInSalesforce}
                data-testid="test-id-your-info-member-phone-type-input"
                label="Phone type"
                value={formData.memberPhoneType}
                onChange={(e) => handleChange("memberPhoneType", e)}
                isRequired
                errorMessage={formErrors.memberPhoneType}
                options={PhoneTypeOptions}
                css={{ flex: 1 }}
                aria-label={labels.ariaPhoneType}
              />
            </FormRow>
            <FormRow isMobile={isMobile} data-testid="test-id-your-info-contact-preference-row">
              <SelectInput
                enableOutsideScroll={canvasIsInSalesforce}
                data-testid="test-id-your-info-member-preferred-contact-method-input"
                label="Preferred method of contact"
                value={formData.memberPreferredContactMethod}
                onChange={(e) => handleChange("memberPreferredContactMethod", e)}
                isRequired
                errorMessage={formErrors.memberPreferredContactMethod}
                options={PreferredContactMethodOptions}
                css={{ flex: 1 }}
                aria-label={labels.ariaContactMethod}
              />
              <SelectInput
                enableOutsideScroll={canvasIsInSalesforce}
                data-testid="test-id-your-info-member-voicemail-preference-input"
                label="Okay to leave voicemails?"
                value={formData.memberLeaveVoicemailPreference}
                onChange={(e) => handleChange("memberLeaveVoicemailPreference", e)}
                isRequired
                errorMessage={formErrors.memberLeaveVoicemailPreference}
                options={YesNoOptions}
                css={{ flex: 1 }}
                aria-label={labels.ariaVoicemailPreference}
              />
            </FormRow>

            {shouldShowParentGuardianSection && (
              <ParentGuardianSection
                data-testid="test-id-your-info-parent-guardian-section"
                isMobile={isMobile}
                formData={displayedParentGuardianData}
                formErrors={parentGuardianErrors}
                onFieldChange={handleParentGuardianChange}
                sameAsPatientAddress={sameAsPatientAddress}
                onSameAsPatientAddressChange={handleSameAsPatientAddressChange}
                isPatientAddressFilled={Boolean(
                  formData.memberStreetAddress1 &&
                    formData.memberCity &&
                    formData.memberState &&
                    formData.memberZipCode,
                )}
                isPolicyHolderDisabled={isGuardianPolicyHolderDisabled}
                isPolicyHolderHidden={isGuardianPolicyHolderHidden}
                canvasIsInSalesforce={canvasIsInSalesforce}
              />
            )}
          </FormSpacing>
        </form>
      </FormCard>
    </Fragment>
  );
};
